import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

/*  tweddi — Auth (/auth)
    tweddi identity: cream ground, Inclusive Sans, near-mono ink, apricot only
    as a rare tiny accent (the ✳), the woven monogram wordmark (halo bg always
    matches what's behind it), tiny uppercase labels, hairline inputs,
    circle-arrow submits. Logic fully preserved.                             */

const API   = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CREAM = '#FFFDF8';
const WASH  = '#FBF6EF';
const INK   = '#141313';
const MUTE  = '#6E6B62';
const APRICOT = '#F4A26B';
const RULE  = 'rgba(20,19,19,0.10)';
const HAIR  = 'rgba(20,19,19,0.06)';

/* woven monogram — pass the true background as `bg` (cream here) */
const Mark = ({ size = 24, mark = INK, bg = CREAM }) => (
  <svg viewBox="-60 -60 664 520" width={size} height={size * (520/664)} style={{ overflow: 'visible', display: 'block', flexShrink: 0 }} aria-label="tweddi">
    <circle cx="200" cy="200" r="108" fill="none" stroke={mark} strokeWidth="66" />
    <circle cx="344" cy="200" r="108" fill="none" stroke={bg}   strokeWidth="108" />
    <circle cx="344" cy="200" r="108" fill="none" stroke={mark} strokeWidth="66" />
    <path d="M 206.01 307.83 A 108 108 0 0 0 282.00 270.28" fill="none" stroke={bg}   strokeWidth="108" />
    <path d="M 196.23 307.93 A 108 108 0 0 0 285.11 266.49" fill="none" stroke={mark} strokeWidth="66" />
  </svg>
);

const FieldLabel = ({ children }) => (
  <p style={{ fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: MUTE, marginBottom: '0.6rem' }}>{children}</p>
);
const CircleArrow = ({ size = 32 }) => (
  <span className="tw-circle" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: size, height: size, borderRadius: '50%', background: INK, flexShrink: 0, transition: 'transform 0.25s cubic-bezier(.16,1,.3,1)' }}>
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={CREAM} strokeWidth="2"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
  </span>
);

export default function AuthPage() {
  const navigate = useNavigate();
  const { user, login, register, updateUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isHost, setIsHost] = useState(false);
  const [activeTab, setActiveTab] = useState('login');
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [registerForm, setRegisterForm] = useState({ name: '', email: '', password: '', promoCode: '' });
  const [showLoginPwd, setShowLoginPwd] = useState(false);
  const [showRegisterPwd, setShowRegisterPwd] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);

  useEffect(() => {
    const hostSignup = localStorage.getItem('isHostSignup');
    if (hostSignup) { setIsHost(true); setActiveTab('register'); localStorage.removeItem('isHostSignup'); }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault(); setIsLoading(true);
    try {
      const user = await login(loginForm.email, loginForm.password);
      toast.success(`Welcome back, ${user.name}`, { duration: 1500 });
      const pendingCode = localStorage.getItem('pendingEventCode');
      if (pendingCode) {
        try {
          const res = await axios.post(`${API}/events/join`, { event_code: pendingCode });
          updateUser({ event_id: res.data.event.id });
          localStorage.removeItem('pendingEventCode');
          navigate(user.profile_complete ? '/discover' : '/profile-setup');
        } catch { localStorage.removeItem('pendingEventCode'); toast.error('Invalid event code'); navigate('/'); }
      } else if (user.is_host) navigate('/admin');
      else if (user.event_id && user.profile_complete) navigate('/discover');
      else if (user.event_id) navigate('/profile-setup');
      else navigate('/');
    } catch (err) { toast.error(err.response?.data?.detail || 'Login failed'); }
    finally { setIsLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault(); setIsLoading(true);
    try {
      const pendingCode = localStorage.getItem('pendingEventCode');
      await register(registerForm.email, registerForm.password, registerForm.name, isHost, pendingCode, isHost && registerForm.promoCode ? registerForm.promoCode : null);
      setRegisteredEmail(registerForm.email); setVerificationSent(true);
    } catch (err) { toast.error(err.response?.data?.detail || 'Registration failed'); }
    finally { setIsLoading(false); }
  };

  const handleResendVerification = async () => {
    setResendLoading(true);
    try { await axios.post(`${API}/auth/resend-verification`, { email: registeredEmail }); toast.success('Verification email resent!'); }
    catch { toast.error('Failed to resend. Please try again.'); }
    finally { setResendLoading(false); }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault(); setForgotLoading(true);
    try { await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail }); setForgotSent(true); }
    catch { setForgotSent(true); }
    finally { setForgotLoading(false); }
  };

  const inputStyle = { width: '100%', border: 'none', borderBottom: `1.5px solid ${RULE}`, background: 'transparent', fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.95rem', fontWeight: 600, color: INK, padding: '0.65rem 0', outline: 'none', letterSpacing: '0.01em', transition: 'border-color 0.2s', caretColor: INK };
  const eyebrow = verificationSent ? 'Almost there' : showForgot ? 'Reset password' : isHost ? 'For couples' : activeTab === 'login' ? 'Welcome back' : 'Create account';

  return (
    <div style={{ background: `linear-gradient(180deg, ${CREAM} 55%, ${WASH} 100%)`, minHeight: '100dvh', maxWidth: '30rem', margin: '0 auto', padding: '0 1.75rem', display: 'flex', flexDirection: 'column', justifyContent: 'center' }} data-testid="auth-page">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inclusive+Sans:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&display=swap');
        * { box-sizing: border-box; }
        .ap-h1 { font-family: 'Inclusive Sans', sans-serif; font-weight: 600; letter-spacing: -0.03em; line-height: 1.04; color: ${INK}; margin: 0; }
        .bb-input:focus { border-bottom-color: ${INK} !important; outline: none; }
        .bb-input::placeholder { color: rgba(20,19,19,0.35); font-weight: 500; }
        .tw-circle:hover { transform: translateX(3px); }
        .bb-tab { cursor: pointer; transition: color 0.2s, border-color 0.2s; }
        .bb-tab:hover { color: ${INK} !important; }
        .bb-link { border-bottom: 1px solid rgba(20,19,19,0.3); cursor: pointer; transition: opacity .2s; }
        .bb-link:hover { opacity: 0.6; }
        .ap-twinkle { display: inline-block; animation: ap-tw 2.6s ease-in-out infinite; transform-origin: center; }
        @keyframes ap-tw { 0%,100%{transform:scale(1) rotate(0);opacity:.7} 50%{transform:scale(1.3) rotate(90deg);opacity:1} }
        @keyframes spin { to { transform: rotate(360deg); } }
        .spin { animation: spin 0.8s linear infinite; }
        @media (prefers-reduced-motion: reduce) { .ap-twinkle { animation: none; } }
      `}</style>

      {/* Back */}
      <button onClick={() => { if (verificationSent) { setVerificationSent(false); setActiveTab('register'); } else if (showForgot) { setShowForgot(false); setForgotSent(false); setForgotEmail(''); } else navigate('/'); }} data-testid="back-btn" style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.62rem', fontWeight: 600, letterSpacing: '0.16em', textTransform: 'uppercase', color: MUTE, display: 'inline-flex', alignItems: 'center', gap: '0.4rem', marginBottom: '3rem', padding: 0, alignSelf: 'flex-start', marginTop: '2rem', transition: 'color 0.2s' }}>
        ← Back
      </button>

      {/* Wordmark */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '2.5rem' }}>
        <Mark size={26} mark={INK} bg={CREAM} />
        <span style={{ fontFamily: "'Inclusive Sans', sans-serif", fontSize: '1.15rem', fontWeight: 600, color: INK, letterSpacing: '-0.01em' }}>tweddi</span>
      </div>

      {/* HEADLINE */}
      <div style={{ marginBottom: '2.5rem', borderTop: `1px solid ${INK}`, paddingTop: '1.5rem' }}>
        <p style={{ fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: MUTE, marginBottom: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          {eyebrow} <span className="ap-twinkle" style={{ fontSize: '0.7rem', color: APRICOT }}>✳</span>
        </p>
        <h1 className="ap-h1" style={{ fontSize: 'clamp(2.3rem, 9vw, 3.1rem)' }}>
          {verificationSent ? <>Check your<br />inbox.</>
            : showForgot ? <>Reset your<br />password.</>
            : isHost ? <>Your wedding,<br />one code.</>
            : activeTab === 'login' ? <>Welcome<br />back.</>
            : <>Join the<br />story.</>}
        </h1>
      </div>

      {/* VERIFICATION SENT */}
      {verificationSent && (
        <div>
          <p style={{ fontFamily: "'Inclusive Sans', sans-serif", fontSize: '1.05rem', fontStyle: 'italic', color: INK, marginBottom: '0.75rem' }}>{registeredEmail}</p>
          <p style={{ fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.78rem', fontWeight: 500, color: MUTE, lineHeight: 1.7, marginBottom: '1.5rem' }}>We've sent a verification link. Click it to activate your account.</p>
          <div style={{ borderTop: `1px solid ${RULE}`, paddingTop: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
            <p style={{ fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.72rem', fontWeight: 500, color: MUTE }}>
              Didn't receive it?{' '}
              <button onClick={handleResendVerification} disabled={resendLoading} style={{ background: 'none', border: 'none', borderBottom: `1px solid ${INK}`, cursor: 'pointer', fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.72rem', fontWeight: 600, color: INK, padding: 0 }}>
                {resendLoading ? 'Sending…' : 'Resend email'}
              </button>
            </p>
            <button onClick={() => { if (user?.is_host) navigate('/admin'); else navigate('/profile-setup'); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.66rem', fontWeight: 600, color: MUTE, textAlign: 'left', letterSpacing: '0.02em' }} className="bb-tab">Skip for now →</button>
          </div>
        </div>
      )}

      {/* FORGOT PASSWORD */}
      {!verificationSent && showForgot && (
        forgotSent ? (
          <div>
            <p style={{ fontFamily: "'Inclusive Sans', sans-serif", fontSize: '1.1rem', fontStyle: 'italic', color: INK, marginBottom: '0.75rem' }}>Check your inbox.</p>
            <p style={{ fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.76rem', fontWeight: 500, color: MUTE, lineHeight: 1.7, marginBottom: '1.5rem' }}>If an account exists for <strong style={{ color: INK, fontWeight: 700 }}>{forgotEmail}</strong>, a reset link is on its way.</p>
            <button onClick={() => { setShowForgot(false); setForgotSent(false); setForgotEmail(''); }} style={{ background: 'none', border: 'none', borderBottom: `1px solid ${INK}`, cursor: 'pointer', fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.66rem', fontWeight: 600, color: INK, padding: '0 0 2px', letterSpacing: '0.06em' }}>← Back to sign in</button>
          </div>
        ) : (
          <form onSubmit={handleForgotPassword} style={{ display: 'flex', flexDirection: 'column', gap: '1.75rem' }}>
            <div>
              <FieldLabel>Email</FieldLabel>
              <input className="bb-input" type="email" placeholder="your@email.com" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} required style={inputStyle} data-testid="forgot-email" />
            </div>
            <button type="submit" disabled={forgotLoading} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.85rem', background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: INK, opacity: forgotLoading ? 0.5 : 1 }} data-testid="forgot-submit">
              {forgotLoading ? 'Sending…' : 'Send reset link'} <CircleArrow size={32} />
            </button>
          </form>
        )
      )}

      {/* LOGIN / REGISTER TABS */}
      {!verificationSent && !showForgot && (
        <>
          <div style={{ display: 'flex', gap: '2rem', marginBottom: '2rem', borderBottom: `1px solid ${RULE}` }}>
            {['login', 'register'].map(tab => (
              <button key={tab} className="bb-tab" onClick={() => setActiveTab(tab)} data-testid={`${tab}-tab`} style={{ background: 'none', border: 'none', borderBottom: `2px solid ${activeTab === tab ? INK : 'transparent'}`, padding: '0 0 0.7rem', fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.64rem', fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: activeTab === tab ? INK : MUTE, cursor: 'pointer', transition: 'all 0.2s', marginBottom: '-1px' }}>
                {tab === 'login' ? 'Sign in' : 'Register'}
              </button>
            ))}
          </div>

          {activeTab === 'login' && (
            <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: '1.75rem' }}>
              <div>
                <FieldLabel>Email</FieldLabel>
                <input className="bb-input" type="email" placeholder="your@email.com" value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} autoComplete="email" required style={inputStyle} data-testid="login-email" />
              </div>
              <div>
                <FieldLabel>Password</FieldLabel>
                <div style={{ position: 'relative' }}>
                  <input className="bb-input" type={showLoginPwd ? 'text' : 'password'} placeholder="Enter password" value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} autoComplete="current-password" required style={{ ...inputStyle, paddingRight: '2rem' }} data-testid="login-password" />
                  <button type="button" onClick={() => setShowLoginPwd(v => !v)} tabIndex={-1} style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: MUTE }}>
                    {showLoginPwd ? <EyeOff size={15} strokeWidth={1.75} /> : <Eye size={15} strokeWidth={1.75} />}
                  </button>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <button type="submit" disabled={isLoading} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.85rem', background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: INK, opacity: isLoading ? 0.5 : 1 }} data-testid="login-submit">
                  {isLoading ? 'Signing in…' : 'Sign in'} <CircleArrow size={32} />
                </button>
                <button type="button" onClick={() => { setShowForgot(true); setForgotEmail(loginForm.email); }} data-testid="forgot-btn" style={{ background: 'none', border: 'none', borderBottom: `1px solid ${RULE}`, cursor: 'pointer', fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.62rem', fontWeight: 500, color: MUTE, padding: '0 0 1px', letterSpacing: '0.02em' }}>
                  Forgot password?
                </button>
              </div>
            </form>
          )}

          {activeTab === 'register' && (
            <form onSubmit={handleRegister} style={{ display: 'flex', flexDirection: 'column', gap: '1.75rem' }}>
              {[
                { label: 'Name', key: 'name', type: 'text', placeholder: 'Your name', testid: 'register-name', autoComplete: 'name' },
                { label: 'Email', key: 'email', type: 'email', placeholder: 'your@email.com', testid: 'register-email', autoComplete: 'email' },
              ].map(f => (
                <div key={f.key}>
                  <FieldLabel>{f.label}</FieldLabel>
                  <input className="bb-input" type={f.type} placeholder={f.placeholder} value={registerForm[f.key]} onChange={(e) => setRegisterForm({ ...registerForm, [f.key]: e.target.value })} autoComplete={f.autoComplete} required style={inputStyle} data-testid={f.testid} />
                </div>
              ))}
              <div>
                <FieldLabel>Password</FieldLabel>
                <div style={{ position: 'relative' }}>
                  <input className="bb-input" type={showRegisterPwd ? 'text' : 'password'} placeholder="Create a password" value={registerForm.password} onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })} autoComplete="new-password" required minLength={6} style={{ ...inputStyle, paddingRight: '2rem' }} data-testid="register-password" />
                  <button type="button" onClick={() => setShowRegisterPwd(v => !v)} tabIndex={-1} style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: MUTE }}>
                    {showRegisterPwd ? <EyeOff size={15} strokeWidth={1.75} /> : <Eye size={15} strokeWidth={1.75} />}
                  </button>
                </div>
              </div>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer' }}>
                <input type="checkbox" className="bb-check" checked={isHost} onChange={(e) => setIsHost(e.target.checked)} data-testid="host-checkbox" style={{ marginTop: '0.15rem', accentColor: INK, flexShrink: 0 }} />
                <span style={{ fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.76rem', fontWeight: 500, color: MUTE, lineHeight: 1.5 }}>I'm hosting a wedding</span>
              </label>
              {isHost && (
                <div>
                  <FieldLabel>Promo code <span style={{ textTransform: 'none', fontWeight: 500, opacity: 0.6, letterSpacing: 0 }}>(optional)</span></FieldLabel>
                  <input className="bb-input" type="text" placeholder="Enter code" value={registerForm.promoCode} onChange={(e) => setRegisterForm({ ...registerForm, promoCode: e.target.value.toUpperCase() })} maxLength={20} style={inputStyle} data-testid="promo-code" />
                </div>
              )}
              {[
                { label: 'I confirm I am 18 years of age or older', testid: 'age-checkbox' },
                { label: <>I agree to the <a href="/terms" style={{ color: INK, textDecoration: 'underline', textUnderlineOffset: '2px' }} onClick={(e) => e.stopPropagation()}>Terms</a> and <a href="/privacy" style={{ color: INK, textDecoration: 'underline', textUnderlineOffset: '2px' }} onClick={(e) => e.stopPropagation()}>Privacy Policy</a></>, testid: 'terms-checkbox' },
              ].map((c, i) => (
                <label key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer' }}>
                  <input type="checkbox" className="bb-check" required data-testid={c.testid} style={{ marginTop: '0.15rem', accentColor: INK, flexShrink: 0 }} />
                  <span style={{ fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.76rem', fontWeight: 500, color: MUTE, lineHeight: 1.5 }}>{c.label}</span>
                </label>
              ))}
              <button type="submit" disabled={isLoading} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.85rem', background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: INK, opacity: isLoading ? 0.5 : 1 }} data-testid="register-submit">
                {isLoading ? 'Creating…' : 'Create account'} <CircleArrow size={32} />
              </button>
            </form>
          )}
        </>
      )}

      {/* Legal footer */}
      <div style={{ marginTop: '3rem', marginBottom: '2rem', paddingTop: '1.5rem', borderTop: `1px solid ${HAIR}`, display: 'flex', gap: '1.5rem' }}>
        <a href="/terms" style={{ fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.58rem', fontWeight: 500, letterSpacing: '0.14em', textTransform: 'uppercase', color: MUTE, textDecoration: 'none' }}>Terms</a>
        <a href="/privacy" style={{ fontFamily: "'Inclusive Sans', sans-serif", fontSize: '0.58rem', fontWeight: 500, letterSpacing: '0.14em', textTransform: 'uppercase', color: MUTE, textDecoration: 'none' }}>Privacy</a>
      </div>
    </div>
  );
}
